import type {
	ApiPriceBook,
	PbLineItem,
	PbLineItemPackage,
	PbLineItemProduct,
	PbPackageProduct,
	PbPricingOption,
	PriceBookData,
	SfTag,
} from '@/apiTypes'
import { PbProductType } from '@/enums/PbProductType'
import { cashAdvanceSortOrder, PbSubcategory } from '@/enums/PbSubcategory'
import { isPbLineItemPackage, isPbLineItemProduct } from '@/guards'
import type { PricingDetails } from '@/mixins/merchandise'

export type PbLineItemProductAndPricingOptions = PbLineItemProduct & {
	pricingOptions: Array<PbPricingOption>
	tags: Array<SfTag>
}

/** Class for holding price bookdata */
export class PriceBook {
	public data: PriceBookData

	public products: PbLineItemProductAndPricingOptions[] = []
	public packages: PbLineItemPackage[] = []
	public packageProducts: PbPackageProduct[] = []

	/** Get all the line items (packages and products) in the price book */
	public getLineItems(): PbLineItem[] {
		return [...this.products, ...this.packages]
	}

	/** Get the top-level price book metadata */
	public getPriceBook(): PriceBookData {
		return this.data
	}

	/** Find the given line item by id */
	public findLineItem(id: string): PbLineItem | undefined {
		return this.getLineItems().find((li) => li.id === id)
	}

	/** Find the given product by id */
	public findProduct(
		id: string
	): PbLineItemProductAndPricingOptions | undefined {
		return this.products.find((li) => li.id === id)
	}

	/** Find the given package by id */
	public findPackage(id: string): PbLineItemPackage | undefined {
		return this.packages.find((li) => li.id === id)
	}

	/** Get all the products in the given package */
	public getProductsInPackage(
		packageId: string
	): Array<PbPackageProduct & { lineItem: PbLineItem | undefined }> {
		return this.packageProducts
			.filter((p) => p.refSpbLinePackage === packageId)
			.map((pp) => ({
				...pp,
				lineItem: this.products.find((p) => p.id === pp.refSpbLineProduct),
			}))
	}

	/** Get all the ids of products in the given package */
	public getProductIdsInPackage(packageId: string) {
		return this.getProductsInPackage(packageId).map((product) => product.id)
	}

	/** Get all the packages that this product is in */
	public getPackagesForProduct(productId: string) {
		return this.packageProducts
			.filter((packProd) => packProd.refSpbLineProduct === productId)
			.map((packProd) => {
				const foundPackage = this.packages.find(
					(pack) => pack.id === packProd.refSpbLinePackage
				)

				if (!foundPackage) throw new Error('Package not found')

				return {
					...packProd,
					package: foundPackage,
				}
			})
	}

	/** Get a list of all unique tags found in items filtered by predicate */
	public getProductTags(
		predicate: (lineItem: PbLineItemProductAndPricingOptions) => boolean = () =>
			true
	): SfTag[] {
		const tags = this.products
			.filter(predicate)
			.map((lineItem) => lineItem.tags)
			.flat(2)

		const dedupedIds = new Set(tags?.map((tag) => tag.id))

		const dedupedTags = Array.from(dedupedIds)
			.map((id) => tags?.find((t) => t.id === id))
			.filter((tag) => tag !== undefined)

		return dedupedTags
	}

	/** Get all the products with the given tag */
	public getProductsWithTag(
		tagId: string
	): PbLineItemProductAndPricingOptions[] {
		return this.products.filter(
			(product) =>
				!product.isStandIn && product.tags.some((tag) => tag.id === tagId)
		)
	}

	/** Get all the products that are in section C of SOGS */
	public getCashAdvanceProducts(
		customItems: PbLineItemProductAndPricingOptions[] = []
	): PbLineItemProductAndPricingOptions[] {
		return this.products
			.filter(
				(p) =>
					!p.isVirtualLine &&
					[
						PbProductType.Afterward,
						PbProductType.CashAdvance,
						PbProductType.Cemetery,
						PbProductType.FamilyTravel,
						PbProductType.Other,
					].includes(p.category)
			)
			.concat(customItems)
			.sort((a, b) => {
				const diff =
					(a.subcategory ? cashAdvanceSortOrder.indexOf(a.subcategory) : 999) -
					(b.subcategory ? cashAdvanceSortOrder.indexOf(b.subcategory) : 999)

				if (diff !== 0) return diff
				return PriceBook.compareName(a, b)
			})
	}

	/** Sort package subitems by sortOrder
	 * Exclude transportationTransfer item if OAP item is shown
	 * If the sortOrder is equal to 9999, the item should not be shown
	 */
	public getSortedPackageProducts(
		packageProducts: PbPackageProduct[],
		includesOapItem: boolean
	): PbPackageProduct[] {
		return (
			packageProducts
				// Drop “placeholder” products
				.filter((p) => p.sortOrder !== 9999)

				// Remove the OAP Transportation-Transfer line
				.filter((p) => {
					if (!includesOapItem) return true
					const lineItem = this.findLineItem(p.refSpbLineProduct)
					return lineItem?.subcategory !== PbSubcategory.TransportationTransfer
				})

				// Preserve original sortOrder but put excluded items last
				.slice()
				.sort((a, b) => {
					const aExcluded = a.isIncludedInPackage === false ? 1 : 0
					const bExcluded = b.isIncludedInPackage === false ? 1 : 0
					if (aExcluded !== bExcluded) return aExcluded - bExcluded
					return (a.sortOrder ?? 999) - (b.sortOrder ?? 999)
				})
		)
	}

	/** Get all the merchandise that aren't caskets or urns */
	public getMiscMerchandise(): PbLineItemProductAndPricingOptions[] {
		return this.products
			.filter(
				(p) =>
					!p.isVirtualLine &&
					p.category === PbProductType.Merchandise &&
					p.subcategory !== PbSubcategory.MerchandiseCasket &&
					p.subcategory !== PbSubcategory.MerchandiseUrn &&
					p.subcategory !== PbSubcategory.MerchandiseOuterBurialContainer
			)
			.sort(PriceBook.compareName)
	}

	public getEssentialsBundles() {
		return this.packages.filter(
			(pkg) => pkg.subcategory === PbSubcategory.PackageEssentials
		)
	}

	public static compareName(
		a: PbLineItemProductAndPricingOptions,
		b: PbLineItemProductAndPricingOptions
	) {
		return a.name.localeCompare(b.name)
	}

	/** Get the price of a bundle by totaling sub-items */
	public getBundlePrice(lineItem: PbLineItemPackage) {
		const packageProducts = this.getProductsInPackage(lineItem.id)

		return packageProducts.reduce<PricingDetails>(
			(acc, product) => {
				let guaranteed = 0
				let nonGuaranteed = 0

				if (product.lineItem?.isGuaranteed) {
					guaranteed = product.fixedPrice ?? product.lineItem?.fixedPrice ?? 0
				} else {
					nonGuaranteed =
						product.fixedPrice ?? product.lineItem?.fixedPrice ?? 0
				}

				const newAccPrice = acc.price + guaranteed + nonGuaranteed

				return {
					price: newAccPrice,
					originalPrice: newAccPrice,
					quantity: acc.quantity,
					guaranteed: acc.guaranteed + guaranteed,
					nonGuaranteed: acc.nonGuaranteed + nonGuaranteed,
				}
			},
			{
				price: 0,
				originalPrice: 0,
				quantity: 1,
				guaranteed: 0,
				nonGuaranteed: 0,
			}
		)
	}

	// Save data and unpack into flat objects
	constructor(data: ApiPriceBook) {
		const dataCopy = structuredClone(data)
		const isV2PriceBook = true

		// Find all package-product junction records
		this.packageProducts =
			dataCopy.lineItems?.data
				.filter(
					(lineItem) =>
						isPbLineItemPackage(lineItem) && lineItem.packageRelations
				)
				.flatMap((lineItem) => lineItem.packageRelations!.data) || []

		// Save all products
		this.products =
			dataCopy.lineItems?.data
				.filter((lineItem) => isPbLineItemProduct(lineItem))
				.map((lineItem) => ({
					...lineItem,

					productImageUrl: lineItem.productImageUrl,

					// Set the pricingOptions to an array (without "data") and sort by price, ascending
					pricingOptions:
						lineItem.pricing?.data?.sort((a, b) => a.price - b.price) || [],

					// Set the tags to an array (without the assignment part)
					tags:
						(lineItem.topicAssignments?.data
							?.map((assignment) => assignment.topic?.data)
							.filter((tag) => tag) as Array<SfTag>) || [],

					// Unset things we are handling elsewhere
					// topicAssignments: undefined
					pricing: undefined,
				})) || []

		// Save all packages
		this.packages =
			dataCopy.lineItems?.data
				.filter((lineItem) => isPbLineItemPackage(lineItem))
				.map((lineItem) => ({
					...lineItem,
					productImageUrl: lineItem.productImageUrl,
					packageProducts: undefined,
					isNoCostEverplans: lineItem.isNoCostEverplans ?? false,

					// Mark bundles and calculate their price
					isBundle: lineItem.fixedPrice === 0,
					fixedPrice: isV2PriceBook
						? lineItem.fixedPrice
						: lineItem.fixedPrice === 0
							? this.getBundlePrice(lineItem).price
							: lineItem.fixedPrice,
				}))
				.sort((a, b) => b.fixedPrice - a.fixedPrice) || []

		// Save base price book properties
		const { lineItems, ...dataWithoutLineItems } = dataCopy
		this.data = dataWithoutLineItems
	}
}
